import './operator.css'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { FALLBACK_BRAND } from '@/lib/brand'

/**
 * The product name for the tab title.
 *
 * This runs OUTSIDE the Inertia page tree, so `useBrand()` (which reads the shared `brand`
 * prop via usePage) is unavailable and the tempting move is a literal — reintroducing exactly
 * the class of bug the brand convention test exists to prevent. Instead we read the value the
 * backend already renders into every head via layouts/_brand_head.html.erb. One source of
 * truth (Rails.configuration.x.brand_name), no literal, and a rename stays a one-line change.
 */
const brandName = (): string => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="application-name"]')
  const value = meta?.content?.trim()
  return value ? value : FALLBACK_BRAND
}

/**
 * Whether THIS document's layout wants Inertia to manage its <title>.
 *
 * The operator layout opts in by rendering `<title inertia="">`; the marketing layout
 * deliberately does not, because its title is governed SEO copy
 * (docs/marketing/2026-07-05-landing3-copy.md §Head) that must reach crawlers byte-for-byte.
 *
 * Why this matters: Inertia's head manager emits a DEFAULT title element on every commit
 * whenever the title callback returns a non-empty string for ''. On a marketing page that
 * would append a second, competing <title> to a carefully-tuned SEO head. Returning '' there
 * makes the head manager emit no title at all, leaving the server's untouched.
 *
 * Safe to compute once: marketing links to the operator app with a plain <a href> (see
 * Marketing3Layout), never an Inertia <Link>, so the ERB layout cannot change within one
 * document lifetime.
 */
const titleIsInertiaManaged = document.querySelector('title[inertia]') !== null

// Build stamp: forces a new content hash on this bundle. Rolled 2026-07-23 to route
// every client past 404s their browsers disk-cached during the 14:00 deploy's rollout
// window (see docs + memory: rollout-404 cache incident). Survives minification by design.
document.documentElement.dataset.build = '2026-07-23-r2'

createInertiaApp({
  /**
   * Tab title. A page supplies only its LEAF name (`<Head title="Dashboard" />`) and the
   * suffix is appended here, so the separator and the brand live in exactly one place.
   *
   * The empty-string case is the default applied to any page that provides no <Head> — see
   * createHeadManager's `collect()`, which seeds its defaults from `titleCallback('')` on
   * every commit, including the one triggered when the previous page's <Head> unmounts. That
   * is what makes adoption SAFE TO STAGE: a page without a <Head> shows the bare brand (todays
   * behaviour) rather than inheriting a stale title from the page navigated away from.
   */
  title: (title) => (title ? `${title} · ${brandName()}` : titleIsInertiaManaged ? brandName() : ''),
  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.tsx', { eager: true })
    return pages[`../pages/${name}.tsx`]
  },
  setup({ el, App, props }) {
    createRoot(el).render(<App {...props} />)
  },
})
